import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";

import FlowStickyHeader from "../checkout/components/FlowStickyHeader";
import FlowPageHead from "../checkout/components/FlowPageHead";
import {
  AddressField,
  DateField,
  SelectField,
  TextField,
} from "../auth/components/fields";
import AvatarPicker from "./components/AvatarPicker";
import DeleteAccountDialog from "./components/DeleteAccountDialog";
import useProfile from "../profile/useProfile";
import { isStubProfile } from "../profile/profileStore";
import { USAGE_OPTIONS } from "../profile/usageOptions";
import {
  addressChanged,
  PROFILE_SCHEMA,
  toFormValues,
  toProfilePatch,
} from "./accountForm";
import { resolveAddress } from "./accountAddress";
import { latestDobForAge } from "../auth/age";
import { logoutUser } from "../../redux/reducers/user-authentication/authentication-action";
import { showSuccessToast } from "../../components/Toast";

import "../checkout/styles.css";
import "../auth/screens/styles.css";
import "./styles.css";

const GENDERS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Prefer not to say", value: "OTHERS" },
];

const MAX_DOB = latestDobForAge();

const QUICK_LINKS = [
  { label: "My orders", to: "/my-orders" },
  { label: "Finance", to: "/finance" },
  { label: "Withdrawal requests", to: "/finance/withdrawals" },
  { label: "My reviews", to: "/my-reviews" },
];

const ratingLabel = ({ avgRating, totalRatings }) => {
  if (!totalRatings) return "No reviews yet";

  const score = Number(avgRating) || 0;

  return `${score.toFixed(1)} · ${totalRatings} review${
    totalRatings === 1 ? "" : "s"
  }`;
};

const ContactRow = ({ label, value, verified }) => (
  <div className="sy-flow-row">
    <div className="sy-flow-row-body">
      <span className="sy-flow-row-label">{label}</span>
      <span className="acct-row-value">{value || "Not added"}</span>
    </div>

    {value && (
      <span
        className={`sy-flow-pill ${
          verified ? "sy-flow-pill-brand" : "sy-flow-pill-warn"
        }`}
      >
        {verified ? "Verified" : "Unverified"}
      </span>
    )}
  </div>
);

const AccountRoute = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile, saving, error, fetchProfile, saveProfile } = useProfile();

  const [avatar, setAvatar] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const stub = isStubProfile(profile);

  const initialValues = useMemo(() => toFormValues(profile), [profile]);

  const signOut = useCallback(() => {
    dispatch(logoutUser(() => window.location.replace("/")));
  }, [dispatch]);

  const submit = async (values) => {
    const address = addressChanged(values, initialValues)
      ? await resolveAddress(
          values.address.trim(),
          values.coords,
          profile.address?.countryCode
        )
      : null;

    const patch = toProfilePatch(values, address);

    if (avatar) patch.profileImage = avatar;

    const ok = await saveProfile(patch);

    if (ok) {
      setAvatar(null);
      showSuccessToast("Your profile has been updated.");
    }
  };

  return (
    <div className="sy-flow acct full-bleed">
      <div className="sy-flow-shell">
        <FlowStickyHeader>
          <FlowPageHead title="Account settings" />
        </FlowStickyHeader>

        <div className="sy-flow-grid">
          <div className="sy-flow-main">
            <section className="sy-flow-card">
              <div className="sy-flow-card-head">
                <h2 className="sy-flow-card-title">Profile</h2>
                <span className="sy-flow-card-note">
                  Visible to suppliers you order from
                </span>
              </div>

              <div className="sy-flow-card-body">
                <AvatarPicker
                  name={profile.fullName}
                  imageUrl={avatar || profile.profileImage}
                  onUploaded={setAvatar}
                  disabled={saving}
                />

                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={PROFILE_SCHEMA}
                  onSubmit={submit}
                >
                  <Form className="auth-screen acct-fields" noValidate>
                    <div className="acct-grid">
                      <TextField
                        name="fullName"
                        label="Full name"
                        placeholder="Ama Mensah"
                        autoComplete="name"
                      />

                      <DateField name="dob" label="Date of birth" max={MAX_DOB} />

                      <SelectField name="gender" label="Gender" options={GENDERS} />

                      <SelectField
                        name="usage"
                        label="Usage"
                        options={USAGE_OPTIONS}
                      />

                      <div className="acct-grid-wide">
                        <AddressField
                          name="address"
                          coordsName="coords"
                          label="Address"
                          placeholder="Start typing your address"
                          mapPicker
                        />
                      </div>
                    </div>

                    {error && <p className="sy-flow-note">{error}</p>}

                    <div className="acct-form-actions">
                      <button
                        type="submit"
                        className="sy-flow-btn sy-flow-btn-primary"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </Form>
                </Formik>
              </div>
            </section>
          </div>

          <aside className="sy-flow-rail">
            <section className="sy-flow-card">
              <div className="sy-flow-card-head">
                <h2 className="sy-flow-card-title">Sign-in details</h2>
              </div>

              <div className="sy-flow-card-body">
                <ContactRow
                  label="Email"
                  value={profile.email}
                  verified={profile.emailVerified}
                />

                <ContactRow
                  label="Phone"
                  value={profile.phoneNumber}
                  verified={profile.phoneNumberVerified}
                />

                <p className="sy-flow-hint">
                  Changing either of these is handled through verification.
                </p>
              </div>
            </section>

            <section className="sy-flow-card">
              <div className="sy-flow-card-head">
                <h2 className="sy-flow-card-title">Shortcuts</h2>
                <span className="sy-flow-card-note">{ratingLabel(profile)}</span>
              </div>

              <div className="sy-flow-card-body acct-links-body">
                <nav className="acct-links">
                  {QUICK_LINKS.map((link) => (
                    <button
                      key={link.to}
                      type="button"
                      className="acct-link"
                      onClick={() => navigate(link.to)}
                    >
                      {link.label}

                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="m6 3 5 5-5 5" />
                      </svg>
                    </button>
                  ))}
                </nav>
              </div>
            </section>
          </aside>
        </div>

        <section className="sy-flow-card acct-danger">
          <div className="sy-flow-card-head">
            <h2 className="sy-flow-card-title">Delete account</h2>
          </div>

          <div className="sy-flow-card-body acct-danger-body">
            <p className="acct-danger-copy">
              Closing your account signs you out on every device and hides your
              orders, reviews and wallet. You will need to verify with a code
              before it goes through.
            </p>

            <button
              type="button"
              className="sy-flow-btn sy-flow-btn-danger"
              disabled={stub}
              onClick={() => setDeleting(true)}
            >
              Delete my account
            </button>
          </div>
        </section>
      </div>

      <DeleteAccountDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onSignedOut={signOut}
        onDeactivated={(message) => {
          setDeleting(false);
          showSuccessToast(message);
          signOut();
        }}
      />
    </div>
  );
};

export default AccountRoute;
